import React, { useState, useEffect } from 'react';
import SEOHead from '../components/SEOHead';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Scale, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TermsOfServicePage = () => {
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState({
    title: 'Terms of Service',
    last_updated: 'February 2026',
    sections: []
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cms/legal/terms`);
        if (res.ok) {
          const data = await res.json();
          if (data && data.sections?.length > 0) {
            setPageData(data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch terms:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const defaultSections = [
    {
      title: '1. Agreement to Terms',
      content: `These Terms of Service ("Terms") constitute a legally binding agreement between you ("User", "you", or "your") and GetCare 360 Pty Ltd ("GetCare 360", "we", "us", or "our") governing your access to and use of the GetCare 360 platform.\n\nBy accessing or using our Platform, you agree to be bound by these Terms. If you do not agree to these Terms, you must not access or use the Platform.`
    },
    {
      title: '2. Eligibility',
      content: `To use our Platform, you must:\n\n• Be at least 18 years of age\n• Be a registered NDIS provider or authorised representative\n• Have the legal authority to enter into this agreement\n• Not be prohibited from using the Platform under Australian law\n• Maintain valid NDIS registration where required`
    },
    {
      title: '3. Account Registration',
      content: `When creating an account, you agree to:\n\n• Provide accurate, current, and complete information\n• Maintain and update your information as necessary\n• Keep your login credentials confidential\n• Accept responsibility for all activities under your account\n• Notify us immediately of any unauthorized use`
    },
    {
      title: '4. Use of the Platform',
      content: `You agree to use the Platform only for lawful purposes and in accordance with these Terms. You must not:\n\n• Use the Platform in violation of any applicable laws\n• Attempt to gain unauthorized access to systems\n• Interfere with or disrupt the Platform's operation\n• Upload malicious code or content\n• Use automated systems to access the Platform without permission`
    },
    {
      title: '5. Intellectual Property',
      content: `The Platform and its content, features, and functionality are owned by GetCare 360 and are protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative works without our express written consent.`
    },
    {
      title: '6. Limitation of Liability',
      content: `To the maximum extent permitted by law, GetCare 360 shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Platform. Our total liability shall not exceed the amount paid by you for the services in the 12 months preceding the claim.`
    },
    {
      title: '7. Termination',
      content: `We may terminate or suspend your account at any time for any reason, including breach of these Terms. Upon termination, your right to use the Platform will immediately cease. All provisions which should survive termination shall remain in effect.`
    },
    {
      title: '8. Contact Us',
      content: `For questions about these Terms of Service, please contact us:\n\nEmail: legal@getcare360.com.au\nPhone: 1800 GET CARE\nAddress: Australia Wide`
    }
  ];

  const sections = pageData.sections?.length > 0 ? pageData.sections : defaultSections;

  return (
    <div className="min-h-screen bg-page-bg">
      <SEOHead 
        title="Terms of Service | GetCare 360 - NDIS Software Platform Terms"
        description="Read GetCare 360's Terms of Service. Understand the terms and conditions for using our NDIS provider management platform."
        keywords="GetCare 360 terms of service, NDIS software terms, platform agreement, disability care software terms"
        canonicalUrl="/terms-of-service"
        page="terms-of-service"
        noIndex={false}
      />
      <Header />
      
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary-navy via-primary-navy to-tech-blue pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Scale className="text-white" size={32} />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{pageData.title}</h1>
          <p className="text-white/70">Last updated: {pageData.last_updated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="animate-spin h-8 w-8 text-tech-blue" />
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">
              {sections.map((section, index) => (
                <div key={index}>
                  <h2 className="text-2xl font-bold text-primary-navy mb-4">{section.title}</h2>
                  <div className="text-gray-600 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage;
